import { useEffect, useMemo } from "react";
import { Building2 } from "lucide-react";

import { useCarrierMe } from "@/api/carrier";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";

const ALL_CENTERS = "all";

interface CenterOption {
  id: string;
  label: string;
  carrierName: string;
}

export function DispatchCenterFilter({
  className,
  triggerClassName,
}: {
  className?: string;
  triggerClassName?: string;
}) {
  const carrierMe = useCarrierMe();
  const { dispatchCenterId, setDispatchCenterId } =
    useOptionalDispatchCenterScope();

  const { centers, hasCarrierWideAccess } = useMemo(() => {
    const rows = new Map<string, CenterOption>();
    let carrierWide = false;
    for (const carrier of carrierMe.data?.carriers ?? []) {
      if (carrier.carrierWide) carrierWide = true;
      for (const center of carrier.allowedDispatchCenters) {
        if (center.isActive === false) continue;
        rows.set(center.id, {
          id: center.id,
          label: center.name || center.code || center.id,
          carrierName: carrier.name || "Carrier",
        });
      }
    }
    return {
      centers: Array.from(rows.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
      hasCarrierWideAccess: carrierWide,
    };
  }, [carrierMe.data]);

  useEffect(() => {
    if (
      dispatchCenterId &&
      centers.length > 0 &&
      !centers.some((center) => center.id === dispatchCenterId)
    ) {
      setDispatchCenterId(null);
    }
  }, [centers, dispatchCenterId, setDispatchCenterId]);

  if (carrierMe.isLoading || centers.length === 0) return null;

  const canChooseAll = hasCarrierWideAccess || centers.length > 1;
  const disabled = !canChooseAll && centers.length === 1;
  const value =
    dispatchCenterId ?? (canChooseAll ? ALL_CENTERS : centers[0].id);

  return (
    <div className={cn("flex items-center", className)}>
      <Select
        value={value}
        onValueChange={(next) =>
          setDispatchCenterId(next === ALL_CENTERS ? null : next)
        }
        disabled={disabled}
      >
        <SelectTrigger
          size="sm"
          className={cn("min-w-[190px] justify-between", triggerClassName)}
          aria-label="Dispatch center filter"
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {canChooseAll && (
            <SelectItem value={ALL_CENTERS}>All centers</SelectItem>
          )}
          {centers.map((center) => (
            <SelectItem key={center.id} value={center.id}>
              {centers.length > 1 && carrierMe.data?.carriers.length
                ? `${center.label} · ${center.carrierName}`
                : center.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
